// Singleton registry of live terminals, keyed by backend session id. Owns the
// imperative handles (xterm.Terminal, WebSocket, FitAddon, a manager-owned
// wrapper <div>) OUTSIDE React, so switching tabs or moving a pane re-parents
// the xterm DOM without dropping the PTY. React panes attach/detach the wrapper;
// only an explicit close disposes. Status is mirrored into terminalRuntimeStore.

import '@xterm/xterm/css/xterm.css';

import { ClipboardAddon } from '@xterm/addon-clipboard';
import { FitAddon } from '@xterm/addon-fit';
import { SearchAddon } from '@xterm/addon-search';
import { Unicode11Addon } from '@xterm/addon-unicode11';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { WebglAddon } from '@xterm/addon-webgl';
import { type IDisposable, type ITheme, Terminal } from '@xterm/xterm';
import ReconnectingWebSocket from 'partysocket/ws';

import {
    FRAME,
    decode,
    encode as encodeRaw,
    type StatusFrame,
} from './terminal-protocol';
import { apiErrorMessage, PASTE_IMAGE_MAX_BYTES, sftpApi } from './api';
import { LocalEcho } from './localEcho';
import {
    buildWsUrl,
    connectionModeFor,
    resolveTransport,
    type TerminalTransport,
} from './terminalEndpoint';
import { perf } from './perfCollector';
import { useAppStore } from '../store';
import { useSettings } from '../stores/settingsStore';
import { useTerminalRuntime } from '../stores/terminalRuntimeStore';
import { toast } from '../stores/toastStore';
import type { ThemeMode } from '../types';

// Cast to the ArrayBuffer-backed variant so it satisfies partysocket's stricter
// send() signature (ArrayBufferView<ArrayBuffer>); the bytes are the same.
const encode = (type: number, payload: Uint8Array | string): Uint8Array<ArrayBuffer> =>
    encodeRaw(type, payload) as Uint8Array<ArrayBuffer>;

interface LiveTerminal {
    hostId: string;
    sessionId: string;
    term: Terminal;
    fit: FitAddon;
    search: SearchAddon;
    ws: ReconnectingWebSocket | null;
    ready: boolean; // SSH PTY is up (server sent STATUS ready) — safe to send input
    transport: TerminalTransport; // where bytes flow: desktop local gateway or api
    directFailed: boolean; // auto-mode relay fallback consumed (once per session)
    directWasReady: boolean; // direct connected once → post-ready drops retry direct
    directHintShown: boolean; // one settings-hint toast per session (direct mode)
    inputQueue: string[]; // keystrokes typed while not ready/connected (never dropped)
    lastRecvAt: number; // for zombie-socket detection
    wrapper: HTMLDivElement;
    opened: boolean;
    webglLoaded: boolean;
    paused: boolean;
    pingTimer: ReturnType<typeof setInterval> | null;
    disposables: IDisposable[];
    onMouseDown: (e: MouseEvent) => void;
    onWheel: (e: WheelEvent) => void;
    onPaste: (e: ClipboardEvent) => void;
    copyOnSelectTimer: ReturnType<typeof setTimeout> | null;
    closedToastShown: boolean; // one "closed by server" toast per closure
    localEcho: LocalEcho;
    ackBytes: number;
    ackTimer: ReturnType<typeof setTimeout> | null;
    disposed: boolean;
}

const registry = new Map<string, LiveTerminal>();
const runtime = () => useTerminalRuntime.getState();

// macOS uses ⌘ for app shortcuts; Windows/Linux use Ctrl(+Shift). Prefer the
// Electron bridge's platform; fall back to the browser.
const isMac =
    (typeof window !== 'undefined' && window.cotty?.platform === 'darwin') ||
    (typeof navigator !== 'undefined' &&
        /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent));

// xterm's WebGL renderer can render a blank canvas on touch / mobile / emulated
// (high-DPR) environments — the terminal shows up empty there. Only use it on
// real desktop pointers; everywhere else the reliable default DOM renderer
// handles output. (Detected once at load; a real phone loads in a touch env.)
const useWebgl =
    typeof matchMedia === 'function' &&
    !matchMedia('(pointer: coarse)').matches &&
    (typeof navigator === 'undefined' || (navigator.maxTouchPoints ?? 0) === 0);

// Flow-control ACK granularity: tell the server we've rendered bytes roughly
// every 64KB (and after a short idle) so it releases its pause. See routes.ts.
const ACK_CHUNK = 64 * 1024;

// Ping payloads are opaque 8-byte nonces matched by value for RTT (see
// perfCollector). The top bit is set so a client nonce can never collide with
// a server keepalive payload (epoch-ms, whose top byte is 0x00 for millennia).
const toHex = (u8: Uint8Array): string =>
    Array.from(u8, (b) => b.toString(16).padStart(2, '0')).join('');

function makeNonce(): Uint8Array<ArrayBuffer> {
    const b = new Uint8Array(8);
    crypto.getRandomValues(b);
    b[0]! |= 0x80;
    return b as Uint8Array<ArrayBuffer>;
}

// Send one RTT-tracked ping (no-op unless the socket is open).
function sendTrackedPing(live: LiveTerminal): void {
    const ws = live.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const nonce = makeNonce();
    perf.pingSent(live.sessionId, toHex(nonce));
    try {
        ws.send(encode(FRAME.PING, nonce));
    } catch {
        /* noop */
    }
}

// WS endpoint selection (relay vs desktop direct) lives in terminalEndpoint.ts.

// Terminal color themes, switched with the app theme. Dark mirrors the cotty
// palette; light is a white terminal with a readable (GitHub-ish) ANSI palette.
const TERMINAL_THEMES: Record<'dark' | 'light', ITheme> = {
    dark: {
        background: '#0a0a0b',
        foreground: '#ededee',
        cursor: '#ff9344',
        cursorAccent: '#0a0a0b',
        selectionBackground: 'rgba(255, 147, 68, 0.26)',
    },
    light: {
        background: '#ffffff',
        foreground: '#1f2328',
        cursor: '#ff8a2e',
        cursorAccent: '#ffffff',
        selectionBackground: 'rgba(255, 147, 68, 0.28)',
        black: '#24292e',
        red: '#d73a49',
        green: '#22863a',
        yellow: '#b08800',
        blue: '#0366d6',
        magenta: '#6f42c1',
        cyan: '#1b7c83',
        white: '#6a737d',
        brightBlack: '#959da5',
        brightRed: '#cb2431',
        brightGreen: '#28a745',
        brightYellow: '#b08800',
        brightBlue: '#0366d6',
        brightMagenta: '#8a63d2',
        brightCyan: '#1b7c83',
        brightWhite: '#1f2328',
    },
};

// Resolve the app's ThemeMode (incl. "system") to a concrete dark/light palette.
const resolveThemeMode = (theme: ThemeMode): 'dark' | 'light' =>
    theme === 'system'
        ? typeof matchMedia === 'function' &&
          matchMedia('(prefers-color-scheme: light)').matches
            ? 'light'
            : 'dark'
        : theme;

// Amber decorations for find-in-terminal matches. Passing `decorations` is
// also what makes SearchAddon emit onDidChangeResults (the match counter).
const SEARCH_DECORATIONS = {
    matchBackground: '#ff934433',
    activeMatchBackground: '#ff9344aa',
    matchOverviewRuler: '#ff9344',
    activeMatchColorOverviewRuler: '#ffb578',
};

function createTerm(): { term: Terminal; fit: FitAddon; search: SearchAddon } {
    const settings = useSettings.getState();
    const term = new Terminal({
        fontFamily: "'JetBrains Mono', 'Cascadia Code', ui-monospace, monospace",
        fontSize: settings.fontSize,
        lineHeight: 1.2,
        cursorBlink: true,
        cursorStyle: settings.cursorStyle,
        allowProposedApi: true,
        convertEol: false,
        scrollback: settings.scrollback,
        macOptionIsMeta: true,
        // On mac, xterm only force-selects (over an app's mouse mode) on
        // Alt+click when this is set — needed by the left-drag selection
        // re-dispatch in ensure() (which sends altKey on mac, shiftKey elsewhere).
        macOptionClickForcesSelection: true,
        theme: TERMINAL_THEMES[resolveThemeMode(useAppStore.getState().theme)],
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    // NOTE: the WebGL addon is loaded later, AFTER the first successful fit
    // (see fitSoon). Loading it before the element has its real size makes its
    // canvas initialize at the wrong dimensions — one cause of the terminal
    // rendering in the top-left corner until you resize.
    term.loadAddon(new Unicode11Addon());
    term.loadAddon(new ClipboardAddon());
    // Underline URLs in the output and open them on ⌘/Ctrl+Click (a plain click
    // is left to the shell/selection — and tmux mouse mode, since the capture
    // `onMouseDown` only re-routes *unmodified* left-clicks). `window.open` opens
    // a new tab on web; in the desktop build it's routed to the OS browser by
    // main.ts's setWindowOpenHandler → shell.openExternal.
    term.loadAddon(
        new WebLinksAddon((event, uri) => {
            const modifierDown = isMac ? event.metaKey : event.ctrlKey;
            if (!modifierDown) return;
            window.open(uri, '_blank', 'noopener,noreferrer');
        }),
    );
    const search = new SearchAddon();
    term.loadAddon(search);
    term.unicode.activeVersion = '11';
    return { term, fit, search };
}

// Multiline-paste guard: anything containing a newline could execute commands
// the moment it lands in the shell, so hold it for confirmation in
// PasteWarningDialog (which calls terminalManager.pasteText to deliver it).
function requestPaste(live: LiveTerminal, text: string): void {
    if (!text) return;
    if (useSettings.getState().warnMultilinePaste && /[\r\n]/.test(text)) {
        runtime().setPendingPaste({ sessionId: live.sessionId, text });
        return;
    }
    live.term.paste(text);
}

// ─── Image paste ─────────────────────────────────────────────────────────────
// The shell runs on a remote SSH host, so a clipboard image can never reach a
// CLI directly — e.g. Claude Code's Ctrl+V reads the HOST's clipboard, which
// is headless. Bridge: upload the image bytes to the host (sftp paste-image
// endpoint), then paste the returned absolute path; CLIs that accept image
// paths in the prompt attach it from there.

const IMAGE_PASTE_MIMES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

// Single-quoted + trailing space: safe at a shell prompt (no expansion) and
// recognized by Claude Code's path detection. Server-minted names never
// contain a quote — the fallback covers exotic home-dir names.
const quotedPathInput = (p: string): string =>
    p.includes("'") ? `"${p}" ` : `'${p}' `;

// Upload a clipboard image to the session host, then paste the quoted remote
// path into the PTY. Toast-reports the outcome; never throws.
async function uploadPastedImage(live: LiveTerminal, blob: Blob): Promise<void> {
    if (blob.size > PASTE_IMAGE_MAX_BYTES) {
        toast.error('Image too large (max 25 MB)');
        return;
    }
    toast.info('Uploading image…', { duration: 2000 });
    try {
        const { data } = await sftpApi.pasteImage(live.hostId, blob);
        const current = registry.get(live.sessionId);
        if (!current || current.disposed) return; // pane closed mid-upload
        // term.paste → onData → feedInput: bracketed-paste wrapped and queued
        // across reconnects like any text paste. Single line — the multiline
        // guard doesn't apply.
        current.term.paste(quotedPathInput(data.path));
        toast.success('Image path pasted', { duration: 2500 });
    } catch (err) {
        toast.error(`Image paste failed: ${apiErrorMessage(err).message}`);
    }
}

// Image-aware paste. ONE clipboard read serves both probes (Firefox prompts
// per read — the old path made exactly one). Text wins when the clipboard
// carries both: Office cell copies put text/plain AND a bitmap render on the
// clipboard, and the text is what a terminal wants; real image pastes
// (screenshots, browser "Copy image") carry no text/plain.
async function smartPaste(live: LiveTerminal): Promise<boolean> {
    if (typeof navigator.clipboard?.read !== 'function') {
        return pasteClipboardText(live);
    }
    let items: ClipboardItems;
    try {
        items = await navigator.clipboard.read();
    } catch (err) {
        // Denied/dismissed → don't re-prompt via readText; anything else
        // (exotic formats) still gets the plain-text attempt.
        if ((err as DOMException | null)?.name === 'NotAllowedError') return false;
        return pasteClipboardText(live);
    }
    if (live.disposed) return false;
    for (const item of items) {
        if (item.types.includes('text/plain')) {
            const text = await (await item.getType('text/plain')).text();
            if (!text) return false;
            requestPaste(live, text);
            return true;
        }
    }
    for (const item of items) {
        const type = item.types.find((t) => IMAGE_PASTE_MIMES.includes(t));
        if (type) {
            void uploadPastedImage(live, await item.getType(type));
            return true;
        }
    }
    return false;
}

// The pre-image text paste path, kept for browsers without clipboard.read().
async function pasteClipboardText(live: LiveTerminal): Promise<boolean> {
    try {
        const text = await navigator.clipboard.readText();
        if (!text) return false;
        requestPaste(live, text);
        return true;
    } catch (err) {
        console.warn('clipboard read failed', err);
        return false;
    }
}

// stdin → server (+ optimistic local echo), with the reconnect input queue.
// The sole input path: xterm's onData and the accessory-key bar both go through
// here so flow control + queueing behave identically for typed and injected keys.
function feedInput(live: LiveTerminal, d: string): void {
    live.localEcho.input(d);
    const ws = live.ws;
    if (live.ready && ws && ws.readyState === WebSocket.OPEN) {
        const frame = encode(FRAME.DATA, d);
        ws.send(frame);
        // Frame length minus the 1-byte type = exact UTF-8 byte count.
        perf.countOut(live.sessionId, frame.byteLength - 1);
    } else if (live.inputQueue.length < 4096) {
        live.inputQueue.push(d);
    }
    runtime().touch(live.sessionId);
}

/**
 * Fit only when the wrapper is laid out AND xterm's renderer has measured the
 * font cell size. Before the first render, `proposeDimensions()` returns
 * undefined (cell width/height are 0) and `fit()` would silently no-op, leaving
 * the grid at its default 80×24 in the top-left. Returning false here keeps the
 * caller retrying until the renderer is ready.
 */
function tryFit(live: LiveTerminal): boolean {
    if (live.disposed || !live.opened) return false;
    const el = live.wrapper;
    if (!el.isConnected || el.clientWidth < 2 || el.clientHeight < 2) return false;
    let dims: { cols: number; rows: number } | undefined;
    try {
        dims = live.fit.proposeDimensions();
    } catch {
        return false;
    }
    if (
        !dims ||
        !Number.isFinite(dims.cols) ||
        !Number.isFinite(dims.rows) ||
        dims.cols < 1 ||
        dims.rows < 1
    ) {
        return false; // renderer/font not measured yet → caller retries
    }
    try {
        live.fit.fit();
        return true;
    } catch {
        return false;
    }
}

// Fit once layout has settled. xterm computes rows/cols from the container, so
// fitting before the box has its final size leaves the grid sized for a smaller
// box — the classic "terminal stuck in the top-left until you resize" bug.
// Retry across frames until the wrapper has a real size, then (on first open)
// load WebGL and re-fit so its canvas is correct, and re-fit once webfonts load.
function fitSoon(
    live: LiveTerminal,
    opts: { focus?: boolean; firstOpen?: boolean } = {},
): void {
    let attempts = 0;
    const run = () => {
        if (live.disposed) return;
        if (!tryFit(live)) {
            // Keep retrying while the layout/renderer settle (~1s worth).
            if (attempts++ < 60) requestAnimationFrame(run);
            return;
        }
        // First good fit done — the renderer has measured cells, so it's now
        // safe to swap in the WebGL renderer (on first open, desktop only).
        if (opts.firstOpen && !live.webglLoaded && useWebgl) {
            live.webglLoaded = true;
            try {
                const webgl = new WebglAddon();
                // If the GL context is lost, drop the addon so xterm falls back
                // to the DOM renderer instead of rendering blank forever.
                webgl.onContextLoss(() => {
                    perf.setRenderer(live.sessionId, 'dom');
                    try {
                        webgl.dispose();
                    } catch {
                        /* noop */
                    }
                });
                live.term.loadAddon(webgl);
                perf.setRenderer(live.sessionId, 'webgl');
            } catch {
                // WebGL unavailable; canvas/DOM renderer stays.
            }
        }
        if (opts.focus) live.term.focus();
        // Follow-up fits to absorb the WebGL renderer swap and the webfont load,
        // both of which nudge cell metrics after the first fit.
        requestAnimationFrame(() => tryFit(live));
        setTimeout(() => tryFit(live), 120);
        document.fonts?.ready.then(() => tryFit(live)).catch(() => {});
    };
    requestAnimationFrame(run);
}

// Push the terminal's current size to the server. The WS URL carries only the
// pre-fit default size, and a RESIZE sent right after fit can arrive before the
// SSH stream exists (where it's dropped) — so we also call this on `ready`.
function sendResize(live: LiveTerminal): void {
    const ws = live.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(
        encode(
            FRAME.RESIZE,
            JSON.stringify({ cols: live.term.cols, rows: live.term.rows }),
        ),
    );
    runtime().setSize(live.sessionId, live.term.cols, live.term.rows);
}

// Tell the server how many bytes xterm has rendered so it can release its
// flow-control pause. Batch at ACK_CHUNK; a short debounce flushes the residual
// so light interactive output never leaves credit un-returned.
function flushAck(live: LiveTerminal): void {
    if (live.ackTimer) {
        clearTimeout(live.ackTimer);
        live.ackTimer = null;
    }
    const n = live.ackBytes;
    live.ackBytes = 0;
    const ws = live.ws;
    if (n <= 0 || !ws || ws.readyState !== WebSocket.OPEN) return;
    const b = new Uint8Array(4);
    new DataView(b.buffer).setUint32(0, n);
    ws.send(encode(FRAME.ACK, b));
}

function maybeAck(live: LiveTerminal): void {
    if (live.ackBytes >= ACK_CHUNK) {
        flushAck(live);
    } else if (!live.ackTimer) {
        live.ackTimer = setTimeout(() => {
            live.ackTimer = null;
            flushAck(live);
        }, 100);
    }
}

// Flush keystrokes buffered while the PTY wasn't ready (initial connect or a
// reconnect), so input is never silently dropped across a blip.
function flushInput(live: LiveTerminal): void {
    const ws = live.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const q = live.inputQueue;
    live.inputQueue = [];
    for (const d of q) {
        const frame = encode(FRAME.DATA, d);
        ws.send(frame);
        perf.countOut(live.sessionId, frame.byteLength - 1);
    }
}

// Direct-transport failure policy (desktop, pre-ready failures only).
// Mode 'direct' (the default): the error stays in the pane — never a silent
// relay fallback — plus a one-time hint toast pointing at host settings.
// Mode 'auto' (explicit setting): flip to relay once per session lifetime and
// redial. If direct was EVER ready this session, keep retrying direct — the
// tmux session lives there, and a post-ready drop means the host blipped,
// not that the transport is wrong. Manual reconnect() resets all of this.
function handleDirectFailure(live: LiveTerminal): void {
    if (live.transport !== 'direct' || live.ready) return;
    const mode = connectionModeFor(live.hostId);
    // Stale transport: the pane was created before the host list synced (or
    // the host was just flipped to Relay in settings). Correct silently — the
    // server refuses credentials for relay hosts anyway.
    if (mode === 'relay') {
        live.transport = 'relay';
        live.ws?.reconnect();
        return;
    }
    if (mode === 'auto' && !live.directFailed && !live.directWasReady) {
        live.directFailed = true;
        live.transport = 'relay';
        toast.info('Direct connection failed — falling back to relay', {
            duration: 5000,
        });
        live.ws?.reconnect();
        return;
    }
    if (mode !== 'auto' && !live.directHintShown) {
        live.directHintShown = true;
        toast.error(
            'Direct connection failed — set Connection to Auto or Relay in Settings to route through the cotty server',
            { duration: 6000 },
        );
    }
}

function openWs(live: LiveTerminal) {
    const { term, hostId, sessionId } = live;
    runtime().setStatus(sessionId, 'connecting');
    runtime().setSize(sessionId, term.cols, term.rows);
    term.writeln('\x1b[90mEstablishing SSH…\x1b[0m');

    // partysocket auto-reconnects (with backoff) and re-fires open/message/close
    // on the SAME instance, so these handlers attach once. The URL is a provider
    // so each reconnect uses the current size + a fresh bearer token — and, on
    // desktop, the CURRENT transport (direct ↔ relay can flip between attempts).
    // tmux makes the reattach seamless (the session survives and redraws).
    const ws = new ReconnectingWebSocket(
        () => buildWsUrl(live.transport, hostId, sessionId, term.cols, term.rows),
        undefined,
        {
            minReconnectionDelay: 500,
            maxReconnectionDelay: 10_000,
            reconnectionDelayGrowFactor: 1.6,
            // We gate input ourselves (flush on the SSH `ready` frame), so don't
            // let partysocket replay queued sends before the PTY exists.
            maxEnqueuedMessages: 0,
        },
    );
    ws.binaryType = 'arraybuffer';
    live.ws = ws;

    ws.onopen = () => {
        live.ackBytes = 0;
        live.lastRecvAt = Date.now();
        // In-flight pings died with the old socket; their echoes never come.
        perf.clearPending(sessionId);
        // Still 'connecting' until the SSH `ready` STATUS frame arrives.
        runtime().setStatus(sessionId, 'connecting');
    };

    ws.onmessage = (e) => {
        if (typeof e.data === 'string') return;
        live.lastRecvAt = Date.now();
        const { type, payload } = decode(e.data as ArrayBuffer);
        switch (type) {
            case FRAME.DATA: {
                live.localEcho.serverData(payload);
                perf.countIn(sessionId, payload.byteLength);
                const n = payload.byteLength;
                // ACK bytes only once xterm has actually rendered them — this is
                // the client→server flow-control signal (respects xterm's write
                // buffer limit and a slow client).
                term.write(payload, () => {
                    live.ackBytes += n;
                    maybeAck(live);
                });
                runtime().touch(sessionId);
                break;
            }
            case FRAME.STATUS: {
                const s = JSON.parse(
                    new TextDecoder().decode(payload),
                ) as StatusFrame;
                if (s.kind === 'ready') {
                    live.ready = true;
                    live.closedToastShown = false;
                    if (live.transport === 'direct') live.directWasReady = true;
                    runtime().setStatus(sessionId, 'ready');
                    flushInput(live); // send anything typed during (re)connect
                    // The PTY is alive now — make sure tmux gets our real size
                    // (the initial RESIZE may have raced ahead of the stream).
                    tryFit(live);
                    sendResize(live);
                    setTimeout(() => {
                        tryFit(live);
                        sendResize(live);
                    }, 150);
                } else if (s.kind === 'error') {
                    runtime().setStatus(sessionId, 'error', s.msg ?? 'error');
                    handleDirectFailure(live);
                } else if (s.kind === 'closed') {
                    runtime().setStatus(sessionId, 'closed');
                    // One toast per closure, even if reconnect attempts land
                    // repeated `closed` frames; reset on the next `ready`.
                    if (!live.closedToastShown) {
                        live.closedToastShown = true;
                        const host = useAppStore
                            .getState()
                            .hosts.find((h) => h.id === hostId);
                        toast.info(
                            `Session closed by server${host ? ` — ${host.name}` : ''}`,
                        );
                    }
                }
                break;
            }
            case FRAME.PING:
                // Either the echo of one of our nonce pings (→ RTT sample) or a
                // server keepalive. NEVER echo back: the server echoes inbound
                // pings too, so a client echo used to bounce forever — every
                // keepalive added another frame orbiting client↔server.
                perf.pingRecv(sessionId, toHex(payload));
                break;
        }
    };

    // partysocket keeps retrying transient errors; real failures arrive as a
    // STATUS 'error' frame, so don't surface a permanent error here.
    ws.onerror = () => {};

    ws.onclose = () => {
        live.ready = false;
        // Transient drop → partysocket reconnects (show 'connecting'); an
        // explicit close() (user disconnect) won't reconnect → 'disconnected'.
        runtime().setStatus(
            sessionId,
            ws.shouldReconnect ? 'connecting' : 'disconnected',
        );
    };

    // stdin → server (+ optimistic local echo). Queue input until the PTY is
    // ready (initial connect AND across reconnects) so keystrokes are never lost.
    live.disposables.push(term.onData((d) => feedInput(live, d)));
    // resize → server; clear the local-echo ghost on resize/scroll.
    live.disposables.push(term.onResize(() => sendResize(live)));
    live.disposables.push(term.onResize(() => live.localEcho.clear()));
    live.disposables.push(term.onScroll(() => live.localEcho.clear()));

    // Keepalive ping + liveness. If nothing has arrived for >30s despite the
    // server pinging every 20s, the socket is a half-open zombie — force a
    // reconnect (partysocket dials a fresh connection).
    live.pingTimer = setInterval(() => {
        // A deliberately-closed socket (user disconnect) must STAY closed.
        // Without this guard the zombie re-dial below would silently resurrect
        // the SSH session ~30s after the user disconnected it (lastRecvAt goes
        // stale the moment the socket closes).
        if (!ws.shouldReconnect) return;
        if (Date.now() - live.lastRecvAt > 30_000) {
            live.lastRecvAt = Date.now();
            ws.reconnect();
            return;
        }
        // Nonce ping: keeps traffic flowing both ways (the server echoes it)
        // AND yields a free RTT sample every 15s via perfCollector.
        sendTrackedPing(live);
    }, 15_000);
}

function ensure(hostId: string, sessionId: string): LiveTerminal {
    let live = registry.get(sessionId);
    if (live && !live.disposed) return live;

    const wrapper = document.createElement('div');
    wrapper.className = 'absolute inset-0';
    const { term, fit, search } = createTerm();

    // Let copy/paste and the command palette work while the terminal is focused;
    // everything else (incl. Ctrl-C/D/R/L) goes to the shell. Returning false
    // makes xterm ignore the key and lets it propagate to the DOM (so the app's
    // window-level ⌘/Ctrl+K listener fires and nothing reaches the shell).
    term.attachCustomKeyEventHandler((e) => {
        if (e.type !== 'keydown') return true;
        const key = e.key.toLowerCase();
        const primary = isMac ? e.metaKey : e.ctrlKey;

        // Command palette: ⌘K (mac) / Ctrl+K (win/linux).
        if (key === 'k' && primary && !e.shiftKey && !e.altKey) return false;

        // Perf HUD: ⌘/Ctrl+Shift+P — handled by the window listener in __root
        // (returning false keeps it from the shell and lets it bubble there).
        if (key === 'p' && primary && e.shiftKey && !e.altKey) return false;

        // Find in terminal: ⌘F (mac) / Ctrl+Shift+F (win/linux). Plain Ctrl+F
        // must keep reaching the shell (readline forward-char, less search).
        const findCombo = isMac
            ? e.metaKey && !e.shiftKey && key === 'f'
            : e.ctrlKey && e.shiftKey && key === 'f';
        if (findCombo && !e.altKey) {
            e.preventDefault();
            runtime().openSearch(sessionId);
            return false;
        }

        // Font size: ⌘/Ctrl +/−/0 ('+' is Shift+'=' on most layouts, so don't
        // exclude shift there). preventDefault blocks browser page-zoom on web.
        if (primary && !e.altKey && (e.key === '+' || e.key === '=')) {
            e.preventDefault();
            useSettings.getState().adjustFontSize(1);
            return false;
        }
        if (primary && !e.altKey && !e.shiftKey && e.key === '-') {
            e.preventDefault();
            useSettings.getState().adjustFontSize(-1);
            return false;
        }
        if (primary && !e.altKey && !e.shiftKey && e.key === '0') {
            e.preventDefault();
            useSettings.getState().resetFontSize();
            return false;
        }

        // Copy: ⌘C (mac) / Ctrl+Shift+C (win/linux). Plain Ctrl+C stays SIGINT.
        const copyCombo = isMac
            ? e.metaKey && !e.shiftKey && key === 'c'
            : e.ctrlKey && e.shiftKey && key === 'c';
        if (copyCombo) {
            const sel = term.getSelection();
            if (sel) {
                e.preventDefault();
                void navigator.clipboard
                    .writeText(sel)
                    .then(() => toast.success('Copied', { duration: 1500 }))
                    .catch(() => {});
            }
            return false;
        }

        // Windows/Linux convention: plain Ctrl+C copies when text is selected,
        // otherwise it stays SIGINT (the terminal interrupt).
        if (
            !isMac &&
            e.ctrlKey &&
            !e.shiftKey &&
            !e.altKey &&
            !e.metaKey &&
            key === 'c' &&
            term.hasSelection()
        ) {
            e.preventDefault();
            const sel = term.getSelection();
            if (sel) {
                void navigator.clipboard
                    .writeText(sel)
                    .then(() => toast.success('Copied', { duration: 1500 }))
                    .catch(() => {});
            }
            return false;
        }

        // Paste: ⌘V / Ctrl+V / Ctrl+Shift+V. Image-aware — a clipboard image
        // uploads to the host and pastes as a path (smartPaste). Alt+V is left
        // alone on purpose: it's Meta-V to terminal apps (macOptionIsMeta).
        if (key === 'v' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            const l = registry.get(sessionId);
            if (l) void smartPaste(l);
            return false;
        }

        return true;
    });

    // Mouse handling while tmux mouse mode is on (kept on for wheel-scroll).
    //
    // Right-click: swallow it in the capture phase so xterm never forwards it to
    // tmux (no tmux menu); the browser still fires `contextmenu`, so the app's
    // React menu (TerminalPaneView) opens.
    //
    // Plain left-drag: with mouse reporting on, xterm forwards the drag to tmux
    // and makes NO selection of its own, so the app can't copy it — the Copy
    // menu item, Ctrl+C, Ctrl+Shift+C and ⌘C all read term.getSelection(), which
    // stays empty. Re-route the drag into a LOCAL xterm selection by stopping the
    // real press (so xterm can't forward it to tmux) and re-dispatching a
    // "force-selection" synthetic mousedown (Shift on win/linux, Alt on mac —
    // xterm's shouldForceSelection). xterm then selects locally AND skips tmux
    // forwarding. Verified against xterm 6 source (and triple-checked by an
    // adversarial review of the bundle):
    //   • detail must be >= 1, else SelectionService.handleMouseDown starts no
    //     selection (a synthetic event defaults detail to 0). We forward the real
    //     event's detail so double/triple-click word/line select still work.
    //   • stopImmediatePropagation on the ORIGINAL is required: preventDefault
    //     does NOT stop xterm's bindMouse handler from sendEvent()-ing to tmux
    //     (it never checks defaultPrevented), so without the stop every drag would
    //     ALSO start a tmux copy-mode selection (the double-selection we hit).
    //   • the synth can't carry focus/menu-close itself — SelectionService calls
    //     stopPropagation on the force path, so it never bubbles to document. We
    //     re-create just those by dispatching a plain mousedown on the container
    //     (wrapper.parentElement, a sibling-ancestor of xterm): it bubbles to the
    //     pane's React onMouseDown (active-pane focus) and the context menu's
    //     document "outside-mousedown" closer, without re-entering xterm.
    // Gated on mouseTrackingMode so that, with mouse reporting off, the native
    // event makes a normal selection untouched (a forced synth would no-op there).
    const onMouseDown = (e: MouseEvent) => {
        if (e.button === 2) {
            e.stopImmediatePropagation();
            return;
        }
        if (
            e.button === 0 &&
            !e.shiftKey &&
            !e.altKey &&
            !e.ctrlKey &&
            !e.metaKey &&
            term.modes.mouseTrackingMode !== 'none'
        ) {
            e.stopImmediatePropagation();
            e.preventDefault();
            (e.target as Element | null)?.dispatchEvent(
                new MouseEvent('mousedown', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    button: 0,
                    buttons: 1,
                    detail: e.detail || 1,
                    clientX: e.clientX,
                    clientY: e.clientY,
                    screenX: e.screenX,
                    screenY: e.screenY,
                    shiftKey: !isMac,
                    altKey: isMac,
                }),
            );
            wrapper.parentElement?.dispatchEvent(
                new MouseEvent('mousedown', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    button: 0,
                }),
            );
        }
    };
    wrapper.addEventListener('mousedown', onMouseDown, true);

    // ⌘/Ctrl+scroll zooms the terminal font. Must be non-passive (and capture,
    // ahead of xterm's own wheel handling) or preventDefault is ignored and the
    // browser page-zooms instead.
    const onWheel = (e: WheelEvent) => {
        const primary = isMac ? e.metaKey : e.ctrlKey;
        if (!primary) return;
        e.preventDefault();
        e.stopPropagation();
        useSettings.getState().adjustFontSize(e.deltaY < 0 ? 1 : -1);
    };
    wrapper.addEventListener('wheel', onWheel, { passive: false, capture: true });

    // Browser-menu paste and the mobile paste bubble bypass the Ctrl+V keydown
    // handler entirely (no key event fires). Catch image pastes here in the
    // capture phase, ahead of xterm's textarea handler; text pastes fall
    // through to xterm untouched. No double-fire with the keydown path: Ctrl+V
    // is preventDefault-ed there, so the browser never synthesizes a paste
    // event for it.
    const onPaste = (e: ClipboardEvent) => {
        const dt = e.clipboardData;
        if (!dt) return;
        // Text wins when both are present (Office copies carry text/plain +
        // a bitmap render) — fall through so xterm's own handler pastes it.
        if (dt.types.includes('text/plain')) return;
        for (let i = 0; i < dt.items.length; i++) {
            const item = dt.items[i];
            if (!item || item.kind !== 'file') continue;
            if (!IMAGE_PASTE_MIMES.includes(item.type)) continue;
            const blob = item.getAsFile();
            if (blob) {
                e.preventDefault();
                e.stopImmediatePropagation();
                const l = registry.get(sessionId);
                if (l) void uploadPastedImage(l, blob);
            }
            return;
        }
    };
    wrapper.addEventListener('paste', onPaste, true);

    const localEcho = new LocalEcho(term, (ms) =>
        perf.recordEcho(sessionId, ms),
    );

    live = {
        hostId,
        sessionId,
        term,
        fit,
        search,
        ws: null,
        ready: false,
        transport: resolveTransport(hostId),
        directFailed: false,
        directWasReady: false,
        directHintShown: false,
        inputQueue: [],
        lastRecvAt: Date.now(),
        wrapper,
        opened: false,
        webglLoaded: false,
        paused: false,
        pingTimer: null,
        disposables: [],
        onMouseDown,
        onWheel,
        onPaste,
        copyOnSelectTimer: null,
        closedToastShown: false,
        localEcho,
        ackBytes: 0,
        ackTimer: null,
        disposed: false,
    };

    // Copy-on-select (opt-in via settings). Debounced — the event fires
    // continuously during a drag — and an empty selection never writes (a
    // deselect must not clobber the clipboard). No toast: far too chatty.
    const created = live;
    created.disposables.push(
        term.onSelectionChange(() => {
            if (!useSettings.getState().copyOnSelect) return;
            if (created.copyOnSelectTimer) clearTimeout(created.copyOnSelectTimer);
            created.copyOnSelectTimer = setTimeout(() => {
                created.copyOnSelectTimer = null;
                const sel = term.getSelection();
                if (sel) void navigator.clipboard.writeText(sel).catch(() => {});
            }, 150);
        }),
    );

    perf.ensureSession(sessionId);
    registry.set(sessionId, live);
    openWs(live);
    return live;
}

export const terminalManager = {
    /** Ensure a live terminal exists and connect it (idempotent). */
    connect(hostId: string, sessionId: string): void {
        ensure(hostId, sessionId);
    },

    /**
     * Mount the managed terminal into `container`. Creates+connects on first
     * call, opens xterm into its wrapper once, then re-parents on every attach.
     */
    attach(hostId: string, sessionId: string, container: HTMLElement): void {
        const live = ensure(hostId, sessionId);
        container.appendChild(live.wrapper);
        let firstOpen = false;
        if (!live.opened) {
            live.term.open(live.wrapper);
            live.opened = true;
            firstOpen = true;
        }
        // Re-fit on every attach (initial mount AND tab-switch re-mounts), once
        // the container has its real size.
        fitSoon(live, { focus: true, firstOpen });
    },

    /** Remove the wrapper from the DOM but KEEP the terminal/PTY alive. */
    detach(sessionId: string): void {
        const live = registry.get(sessionId);
        live?.wrapper.remove();
    },

    fit(sessionId: string): void {
        const live = registry.get(sessionId);
        if (live) tryFit(live);
    },

    focus(sessionId: string): void {
        registry.get(sessionId)?.term.focus();
    },

    selectAll(sessionId: string): void {
        registry.get(sessionId)?.term.selectAll();
    },

    hasSelection: (sessionId: string): boolean =>
        registry.get(sessionId)?.term.hasSelection() ?? false,

    async copy(sessionId: string): Promise<boolean> {
        const sel = registry.get(sessionId)?.term.getSelection();
        if (!sel) return false;
        try {
            await navigator.clipboard.writeText(sel);
            toast.success('Copied', { duration: 1500 });
            return true;
        } catch {
            return false;
        }
    },

    /** Image-aware paste (context menu): a clipboard image uploads to the
     *  host and pastes as a path; text takes the normal guarded paste. */
    async paste(sessionId: string): Promise<boolean> {
        const live = registry.get(sessionId);
        if (!live) return false;
        return smartPaste(live);
    },

    /** Deliver text to the PTY directly, bypassing the multiline-paste guard
     *  (used by PasteWarningDialog once the user confirms). */
    pasteText(sessionId: string, text: string): void {
        registry.get(sessionId)?.term.paste(text);
    },

    /** Inject raw bytes/sequences into the PTY (e.g. the mobile accessory key
     *  bar: Esc=\x1b, Ctrl-C=\x03, arrows=\x1b[A …). Uses the same flow-control
     *  + reconnect-queue path as typed keys; do NOT route control codes through
     *  paste (which may bracketed-paste-wrap them). */
    sendInput(sessionId: string, data: string): void {
        const live = registry.get(sessionId);
        if (live) feedInput(live, data);
    },

    /** Scroll the viewport by n lines (negative = up into scrollback). Backs
     *  one-finger touch-drag scrolling. (The accessory bar's PgUp/PgDn send the
     *  CSI sequences to the PTY instead, so full-screen apps page correctly.) */
    scrollLines(sessionId: string, n: number): void {
        registry.get(sessionId)?.term.scrollLines(n);
    },

    // ─── Find in terminal ───────────────────────────────────────────────────
    searchNext(
        sessionId: string,
        query: string,
        opts: { incremental?: boolean } = {},
    ): void {
        const live = registry.get(sessionId);
        if (!live || !query) return;
        live.search.findNext(query, {
            incremental: opts.incremental,
            decorations: SEARCH_DECORATIONS,
        });
    },

    searchPrev(sessionId: string, query: string): void {
        const live = registry.get(sessionId);
        if (!live || !query) return;
        live.search.findPrevious(query, { decorations: SEARCH_DECORATIONS });
    },

    searchClear(sessionId: string): void {
        const live = registry.get(sessionId);
        if (!live) return;
        live.search.clearDecorations();
        live.search.clearActiveDecoration();
    },

    /** Subscribe to match-count updates ("3/14") for the search bar. */
    onSearchResults(
        sessionId: string,
        cb: (r: { resultIndex: number; resultCount: number }) => void,
    ): IDisposable | null {
        const live = registry.get(sessionId);
        return live ? live.search.onDidChangeResults(cb) : null;
    },

    /** One RTT-tracked ping, used by the perf HUD's prober while open. */
    sendPing(sessionId: string): void {
        const live = registry.get(sessionId);
        if (live && !live.disposed) sendTrackedPing(live);
    },

    /** Re-dial a dropped/closed session immediately (tmux makes it seamless). */
    reconnect(sessionId: string): void {
        const live = registry.get(sessionId);
        if (!live || live.disposed) return;
        live.ready = false;
        // A manual reconnect re-evaluates the transport from the host's
        // current connection mode and re-arms the auto-fallback.
        live.transport = resolveTransport(live.hostId);
        live.directFailed = false;
        live.directWasReady = false;
        live.directHintShown = false;
        // lastRecvAt went stale while disconnected — refresh it so the zombie
        // check doesn't immediately re-dial on top of this connect attempt.
        live.lastRecvAt = Date.now();
        runtime().setStatus(sessionId, 'connecting');
        if (live.ws) live.ws.reconnect();
        else openWs(live);
    },

    clear(sessionId: string): void {
        registry.get(sessionId)?.term.clear();
    },

    /** Close the WebSocket but keep the terminal (tmux survives → reconnectable). */
    disconnect(sessionId: string): void {
        const live = registry.get(sessionId);
        if (!live) return;
        try {
            live.ws?.close(1000, 'user-disconnect');
        } catch {
            /* noop */
        }
    },

    /** Fully tear down: close WS, dispose xterm, drop from the registry. */
    dispose(sessionId: string): void {
        const live = registry.get(sessionId);
        if (!live) return;
        live.disposed = true;
        if (live.pingTimer) clearInterval(live.pingTimer);
        if (live.ackTimer) clearTimeout(live.ackTimer);
        if (live.copyOnSelectTimer) clearTimeout(live.copyOnSelectTimer);
        live.disposables.forEach((d) => {
            try {
                d.dispose();
            } catch {
                /* noop */
            }
        });
        const ws = live.ws;
        live.ws = null;
        if (ws) {
            ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
            try {
                ws.close();
            } catch {
                /* noop */
            }
        }
        try {
            live.term.dispose();
        } catch {
            /* noop */
        }
        live.wrapper.removeEventListener('mousedown', live.onMouseDown, true);
        live.wrapper.removeEventListener('wheel', live.onWheel, true);
        live.wrapper.removeEventListener('paste', live.onPaste, true);
        live.localEcho.dispose();
        live.wrapper.remove();
        registry.delete(sessionId);
        perf.removeSession(sessionId);
        runtime().remove(sessionId);
    },

    /** Tear down every live terminal — closes all sockets (used on sign-out). */
    disposeAll(): void {
        for (const id of Array.from(registry.keys())) {
            terminalManager.dispose(id);
        }
    },

    /** Recolor every live terminal when the app theme changes. */
    applyTheme(mode: 'dark' | 'light'): void {
        const theme = TERMINAL_THEMES[mode];
        for (const live of registry.values()) live.term.options.theme = theme;
    },
};

// Keep live terminals in sync with the app theme (dark ↔ light).
useAppStore.subscribe((s, p) => {
    if (s.theme !== p.theme) terminalManager.applyTheme(resolveThemeMode(s.theme));
});

// Push settings changes (font size, scrollback, cursor) into every live
// terminal. The refit fires term.onResize → sendResize, so tmux learns the
// new grid without any extra plumbing.
useSettings.subscribe((s, p) => {
    if (
        s.fontSize === p.fontSize &&
        s.scrollback === p.scrollback &&
        s.cursorStyle === p.cursorStyle
    ) {
        return;
    }
    for (const live of registry.values()) {
        live.term.options.fontSize = s.fontSize;
        live.term.options.scrollback = s.scrollback;
        live.term.options.cursorStyle = s.cursorStyle;
        tryFit(live);
    }
});
